import React, { useEffect, useState } from 'react';
import { Col, Container, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import ActionMenu from '../../../components/ActionMenu/ActionMenu';
import ActionMenuItem from '../../../components/ActionMenu/ActionMenuItem';
import Authorize from '../../../components/AuthComponent/Authorize';
import SaveButton from '../../../components/Buttons/SaveButton';
import DataTable from '../../../components/DataTable/DataTable';
import { IColInfo } from '../../../components/DataTable/Interfaces';
import ButtonBar from '../../../components/Fields/ButtonBar';
import SelectListField from '../../../components/Fields/SelectListField';
import { FieldConfig, FormConfig } from '../../../components/Fields/Types';
import Form from '../../../components/Form/Form';
import useSelectListOptions from '../../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../../components/Hooks/ValidationHook/useValidation';
import SpinnerOverlay from '../../../components/Spinner/SpinnerOverlay';
import Translate from '../../../components/Translate';
import { ApplicationFunctions, useAuth } from '../../../contexts/authContext';
import { useNotifications } from '../../../contexts/notificationContext';
import BillOfMaterialItemModel from '../../../models/BillOfMaterialItemModel';
import BillOfMaterialModel from '../../../models/BillOfMaterialModel';
import DataService from "../../../services/data.service";
import IdParams from '../../../types/IdParams';
import { DataResult } from '../../../types/Result';
import Unauthorized from '../../Authentication/Unauthorized';
import BillOfMaterialItem from './BillOfMaterialItem';
import ItemRevision_popup_AddItemToBOM from '../Popups/ItemRevision_popup_AddItemToBOM';
import IconButton from '../../../components/Buttons/IconButton';

interface Props {
    id: number
}

const BillOfMaterialsView: React.FC<Props> = ({id}) => {
    const [bomId, setBomId] = useState(id);
    const [formData, setFormData] = useState<BillOfMaterialModel>();
    const [serverErrors, setServerErrors] = useState<DataResult<BillOfMaterialModel>>({ isSuccesful: true, messages: [], data: undefined });
    const [showSpinner, setShowSpinner] = useState(false);
    const [showAddItemPopup, setShowAddItemPopup] = useState<boolean>(false)
    const { handleResult, handleException } = useNotifications();
    const { isAuthorizedForItemType } = useAuth();
    const navigate = useNavigate()

    const billOfMaterials = useSelectListOptions('api/v1/selectlist/BillOfMaterials/?itemId=' + formData?.itemId ?? 0);

    useEffect(() => {
        setBomId(id)
    }, [id]);

    useEffect(() => {
        getFormData();
    }, [bomId]);

    function getFormData() {
        if (bomId !== 0) {
            setShowSpinner(true);
            DataService.getById<BillOfMaterialModel>('api/v1/BillOfMaterials/', bomId)
                .then(response => {
                    setShowSpinner(false);
                    console.log('bom: ' + bomId, response)
                    setFormData(response);
                })
                .catch(e => {
                    setShowSpinner(false);
                    handleException(e);
                });
        }
    }
    function onBomChange(value: number) {
        setBomId(value);
    }

    function submit() {
        setShowSpinner(true);
        if (formData) {
            DataService
                .update<BillOfMaterialModel>(`api/v1/billOfMaterials/`, formData)
                .then(response => {
                    setShowSpinner(false);
                    handleResult(response);
                    setServerErrors(response);
                    if (response.isSuccesful === true && response.data)
                        setFormData(response.data);
                })
                .catch(e => {
                    setShowSpinner(false);
                    handleException(e);
                });
        }
    }
    let fields: FieldConfig[] = [];

    if (formData && formData.billOfMaterialItems) {
        formData.billOfMaterialItems.forEach((bomItem, bomItemIndex) => {
            fields.push({ name: `billOfMaterialItems.${bomItemIndex}.quantity`, label: 'Quantity', labelSize: 4 });
            fields.push({ name: `billOfMaterialItems.${bomItemIndex}.itemRevisionId`, label: 'Revision', labelSize: 4 });

            bomItem.userDefinedFields.forEach((udf, udfIndex) => {
                let label = ''
                if (udf.userDefinedField.name) {
                    label = udf.userDefinedField.name;
                }
                fields.push({ name: `billOfMaterialItems.${bomItemIndex}.userDefinedFields.${udfIndex}.value`, label: label, labelSize: 4 });
            })
        });
    }

    const formConfig: FormConfig = {
        fields: fields,
        handleSubmit: submit,
        showErrors: 'always',
    };

    const { getLabelProps, getControlProps, getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    if (!formData)
        return <SpinnerOverlay />

    let items = formData.billOfMaterialItems.map((x, index) => {
        return <tr key={x.id}><BillOfMaterialItem bomItem={x} bomItemIndex={index} getFieldProps={getFieldProps} getControlProps={getControlProps} refreshFormData={getFormData} /></tr>
    })

    if (!isAuthorizedForItemType(ApplicationFunctions.ViewBillOfMaterial, formData.itemRevision.item.itemTypeId))
        return (<Unauthorized />);

    return <>
        <Container fluid>
            <Row>
                <Col md={2}>
                    <img style={{ width: '100%' }} src={formData.itemImageUrl} />
                </Col>
                <Col md={10}>
                    <SelectListField labelProps={{ text: 'BILL-OF-MATERIALS VERSION', labelSize: 2, required: false }} controlProps={{ name: 'bomId', value: bomId, onChange: onBomChange, validationResults: [] }} options={billOfMaterials} />
                    <table className={"table table-bordered"} style={{ marginTop: 20 }}>
                        <thead>
                            <tr>
                                <th style={{ width: 5 }}></th>
                                <th><Translate>Item</Translate></th>
                                <th style={{ width: 100 }}><Translate>Quantity</Translate></th>
                                <th><Translate>Part</Translate></th>
                                <th style={{ width: 200 }}><Translate>Version</Translate></th>
                                <th><Translate>Extra info</Translate></th>
                                <th><Translate>Reference pictures</Translate></th>
                                <th className='button-column'></th>
                                <th className='button-column'>
                                    <OverlayTrigger placement="top" overlay={(<Tooltip id="tooltip">Add</Tooltip>)} key={'checkboxButton'}>
                                        <span style={{ color: '#26A69A' }} onClick={() => setShowAddItemPopup(true)}><i className={'fa fa-plus-square'} style={{ margin: '10px 0' }} /></span>
                                    </OverlayTrigger>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {items}
                        </tbody>
                    </table>
                </Col>
            </Row>
        </Container>
        <div style={{marginTop:5}}>
            <ButtonBar offset={2}>
                <Authorize applicationFunction={ApplicationFunctions.UpdateBillOfMaterial} itemTypeId={formData ? formData.itemRevision.item.itemTypeId : 0}>
                    <SaveButton  {...getSubmitButtonProps()} />
                </Authorize>
            </ButtonBar>
        </div>
        {showSpinner && <SpinnerOverlay />}
        {showAddItemPopup && <ItemRevision_popup_AddItemToBOM billOfMaterialId={bomId} bom={formData} show={showAddItemPopup} onHide={() => { getFormData(); setShowAddItemPopup(false); }} />}
    </>
}
export default BillOfMaterialsView;